/**
 * MeasuredBaselineSimulator — REBUILT (2026-05-31)
 *
 * The "Simulator" tab. Mirrors the Advanced tab's left-inputs / right-charts
 * layout but with a far simpler input contract:
 *
 *   AI cost = Σ_agents (cost_per_conv × convs_per_user_per_month × users × tenants)
 *           + platform features (title/summary/insights, Haiku-based, from
 *             pricing constants — NOT user input)
 *
 *   Infra  = same math as the Advanced engine (DB + GCS + Cloud Run + Redis)
 *
 *   Setup  = one-time file embedding + enrichment + bundle context costs.
 *            Shown alongside "first-month total"; excluded from recurring.
 *
 * Per-agent the user provides:
 *   • Source: pick an agent from measured data, or type a cost manually
 *   • Optional model swap (re-prices the same token shape against any other
 *     model — server-side via /measured-baseline?target_model=…)
 *   • Conversations per user per month
 *   • Files per tenant + avg MB (drives infra + setup costs)
 *
 * Shared inputs: active users, num tenants, summaries-on/off, # insight
 * templates, file churn %, concurrent %.
 *
 * Save the resulting scenario → flows into the Roadmap tab as a phase.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Slider,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message as antdMessage,
  theme,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  CloudServerOutlined,
  SwapOutlined,
  ExperimentOutlined,
  EditOutlined,
  TeamOutlined,
  AppstoreOutlined,
  InfoCircleOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  chatbotPickerService,
  type ChatbotPickerOption,
  type MeasuredBaselineResponse,
  type SimpleSimResponse,
} from '../services/chatbotPickerService';
import { useLanguage } from '@/hooks';
import { scenarioService } from '../services/scenarioService';
import { ModelPriceReference } from './ModelPriceReference';
import { ScenarioCard } from './ScenarioCard';
import type { TFunc } from '@/utils/tfunc';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Color palette — identical to the Advanced tab's CostBreakdown so the two
// simulators are visually interchangeable. Keep in lockstep with
// CostBreakdown.tsx::COLORS.
const COLORS = {
  mainAI: '#6366f1',
  internalOps: '#8b5cf6',
  embeddings: '#a78bfa',
  insights: '#f59e0b',
  flows: '#10b981',
  transcription: '#06b6d4',
  infrastructure: '#64748b',
};

// ─── Formatters ──────────────────────────────────────────────────────────
// fmtUsd never rounds: 6 decimals always so sub-cent costs survive display.
function fmtUsd(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '$0.000000';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const [whole, decimals] = abs.toFixed(6).split('.');
  return `${sign}$${Number(whole).toLocaleString('en-US')}.${decimals}`;
}

function fmtN(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return Math.round(n).toLocaleString('en-US');
}

// ─── Per-agent state ─────────────────────────────────────────────────────
// Each row in the "Agents" panel — the user can add multiple, each with its
// own measured/manual source, its own model swap, and its own files config.

type AgentSource = 'measured' | 'manual';

interface SimAgent {
  id: string;
  label: string;
  source: AgentSource;
  // Measured-mode state
  chatbotId: string | null;
  dateRange: [Dayjs, Dayjs];
  targetModel: string | null;
  baseline: MeasuredBaselineResponse | null;
  baselineLoading: boolean;
  // Manual-mode state
  manualCost: number;
  // Shared per-agent
  convsPerUserPerMonth: number;
  filesCount: number;
  avgFileMb: number;
}

function newAgentRow(idSeed: number): SimAgent {
  return {
    id: `a${idSeed}-${Date.now().toString(36)}`,
    label: `Agent ${idSeed}`,
    source: 'measured',
    chatbotId: null,
    // Default to "last 7 days" (per user). The picker supports time-of-day
    // precision so users can sample a specific hour window if they want.
    dateRange: [dayjs().subtract(7, 'day'), dayjs()],
    targetModel: null,
    baseline: null,
    baselineLoading: false,
    manualCost: 0.04,
    convsPerUserPerMonth: 15,
    filesCount: 0,
    avgFileMb: 1.0,
  };
}

// Resolve the effective cost-per-conv for an agent based on its current
// source + measured baseline + optional model swap.
function resolveCostPerConv(a: SimAgent): number {
  if (a.source === 'manual') return a.manualCost;
  if (!a.baseline || a.baseline.baseline.conversation_count === 0) return 0;
  // When a model swap is active, prefer the alt_cost (same token shape,
  // different model rates) — that's the whole point of the swap.
  if (a.targetModel && a.baseline.alt_cost) {
    return a.baseline.alt_cost.avg_total_cost_usd;
  }
  return a.baseline.baseline.avg_total_cost_usd;
}

function resolveModelLabel(a: SimAgent): string {
  if (a.source === 'manual') return 'manual';
  if (!a.baseline) return '—';
  if (a.targetModel && a.baseline.alt_cost) {
    return `${a.baseline.alt_cost.provider}/${a.baseline.alt_cost.model}`;
  }
  if (a.baseline.model_distribution.length > 1) {
    return `Mixed: ${a.baseline.model_distribution.length} models`;
  }
  return a.baseline.baseline_model
    ? `${a.baseline.baseline_provider}/${a.baseline.baseline_model}`
    : '—';
}

// ─── Props ───────────────────────────────────────────────────────────────
export interface SimpleSimSnapshot {
  /** Simple-sim params (carries `_kind:'simple'` so the advisor dispatches correctly). */
  params: any;
  /** Live `/simple-simulate` result for that params (or null when no result yet). */
  breakdown: SimpleSimResponse | null;
}

export interface MeasuredBaselineSimulatorProps {
  pricingPayload: { models: Array<{ id: string; name: string; provider: string }> };
  /** Called whenever the current scenario snapshot changes — the page uses this to
   *  feed the Cost Advisor the EXACT scenario the user is looking at. */
  onSnapshotChange?: (snap: SimpleSimSnapshot) => void;
}

// ─── Component ───────────────────────────────────────────────────────────
export function MeasuredBaselineSimulator({ pricingPayload, onSnapshotChange }: MeasuredBaselineSimulatorProps) {
  const { token } = theme.useToken();
  const { t } = useLanguage();

  // Chatbot picker options (shared across all agent rows)
  const [chatbots, setChatbots] = useState<ChatbotPickerOption[]>([]);
  useEffect(() => {
    chatbotPickerService.listChatbots()
      .then(setChatbots)
      .catch(() => antdMessage.error(t('measuredSim.failedToLoadChatbots')));
  }, []);

  // Saved scenarios — only the new simple-sim ones are shown in the
  // Load library here. Advanced-shape scenarios live in their own tab.
  useEffect(() => {
    setScenariosLoading(true);
    scenarioService.list()
      .then((all) => setSavedScenarios(
        (all || []).filter((s: any) => s?.parameters?._kind === 'simple')
      ))
      .catch(() => { /* fail silently — modal will just show empty state */ })
      .finally(() => setScenariosLoading(false));
  }, []);

  /** Hydrate the simulator's state from a saved simple-sim scenario. */
  function loadScenarioIntoSimulator(s: any) {
    const p = s?.parameters || {};
    if (p._kind !== 'simple') {
      antdMessage.warning(t('measuredSim.oldAdvancedScenario'));
      return;
    }
    // Volume + features
    if (typeof p.active_users === 'number') setActiveUsers(p.active_users);
    if (typeof p.num_tenants === 'number') setNumTenants(p.num_tenants);
    if (typeof p.summaries_enabled === 'boolean') setSummariesEnabled(p.summaries_enabled);
    if (typeof p.insight_templates_count === 'number') {
      setInsightTemplatesEnabled(p.insight_templates_count > 0);
      setInsightTemplatesCount(p.insight_templates_count);
    }
    if (typeof p.file_churn_pct === 'number') setFileChurnPct(p.file_churn_pct);
    if (typeof p.concurrent_pct === 'number') setConcurrentPct(p.concurrent_pct);
    // Agents — rebuild from saved shape. Each saved agent had cost+volume+files;
    // we restore them as MANUAL agents so the loaded scenario reproduces the
    // EXACT cost-per-conv that was saved (rather than re-fetching live data
    // that might differ from when the snapshot was taken).
    if (Array.isArray(p.agents) && p.agents.length > 0) {
      setAgents(p.agents.map((sa: any, idx: number): SimAgent => ({
        id: sa.id || `loaded-${idx}-${Date.now().toString(36)}`,
        label: sa.label || `Agent ${idx + 1}`,
        source: 'manual',
        chatbotId: null,
        dateRange: [dayjs().subtract(7, 'day'), dayjs()],
        targetModel: null,
        baseline: null,
        baselineLoading: false,
        manualCost: Number(sa.cost_per_conv_usd ?? 0),
        convsPerUserPerMonth: Number(sa.convs_per_user_per_month ?? 15),
        filesCount: Number(sa.files_count ?? 0),
        avgFileMb: Number(sa.avg_file_mb ?? 1.0),
      })));
      setNextSeed(p.agents.length + 1);
    }
    setLoadOpen(false);
    antdMessage.success(`Loaded "${s.name}"`);
  }

  // Multi-agent state — start with one row so the page isn't empty.
  const [agents, setAgents] = useState<SimAgent[]>(() => [newAgentRow(1)]);
  const [nextSeed, setNextSeed] = useState(2);

  // Shared volume + infra inputs
  const [activeUsers, setActiveUsers] = useState<number>(100);
  const [numTenants, setNumTenants] = useState<number>(1);
  const [fileChurnPct, setFileChurnPct] = useState<number>(5);
  const [concurrentPct, setConcurrentPct] = useState<number>(100);

  // Platform-feature assumptions
  const [summariesEnabled, setSummariesEnabled] = useState<boolean>(true);
  // Insight templates: on/off toggle that mirrors the auto-summary toggle.
  // When OFF, the count input is hidden and the cost is 0.
  const [insightTemplatesEnabled, setInsightTemplatesEnabled] = useState<boolean>(true);
  const [insightTemplatesCount, setInsightTemplatesCount] = useState<number>(1);

  // Simulate result + loading
  const [result, setResult] = useState<SimpleSimResponse | null>(null);
  const [simLoading, setSimLoading] = useState(false);

  // Save-as-scenario modal
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saving, setSaving] = useState(false);
  // Load-from-library modal
  const [loadOpen, setLoadOpen] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState<any[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);

  // ── Fetch measured baseline for an agent whenever its measured inputs change ──
  useEffect(() => {
    agents.forEach((a, idx) => {
      if (a.source !== 'measured' || !a.chatbotId) return;
      // Skip if already loading or baseline matches current inputs (cheap fingerprint check)
      // Fingerprint uses minute-precision ISO timestamps so swapping the
      // picker's time of day re-fetches even if the day didn't change.
      const fp = `${a.chatbotId}|${a.dateRange[0].toISOString()}|${a.dateRange[1].toISOString()}|${a.targetModel ?? ''}`;
      if ((a as any)._fp === fp) return;
      // Mark loading + fingerprint up-front to avoid re-firing on every render
      setAgents((prev) => prev.map((x, i) => i === idx ? { ...x, baselineLoading: true, _fp: fp } as any : x));
      chatbotPickerService.getMeasuredBaseline({
        chatbotId: a.chatbotId,
        // Send full ISO datetime (including time-of-day) — backend parses
        // both date-only and full ISO. With time precision the user can
        // sample any window down to the minute.
        dateFrom: a.dateRange[0].toISOString(),
        dateTo: a.dateRange[1].toISOString(),
        targetModel: a.targetModel ?? undefined,
      }).then((b) => {
        setAgents((prev) => prev.map((x, i) =>
          i === idx ? { ...x, baseline: b, baselineLoading: false } : x,
        ));
      }).catch((err) => {
        antdMessage.error(err?.response?.data?.error ?? t('measuredSim.failedToLoadBaseline'));
        setAgents((prev) => prev.map((x, i) => i === idx ? { ...x, baselineLoading: false } : x));
      });
    });
    // Dep array uses the per-agent fingerprint so we don't refetch on unrelated state changes.
  }, [agents.map(a => `${a.source}|${a.chatbotId}|${a.dateRange[0]?.toISOString()}|${a.dateRange[1]?.toISOString()}|${a.targetModel}`).join('||')]);

  // ── Fire /simple-simulate whenever inputs change ──
  // Debounced by relying on React's batching of state updates within a render;
  // the simulate endpoint is fast (<50ms) so no further throttling needed.
  useEffect(() => {
    const ready = agents.every(a =>
      a.source === 'manual' || (a.baseline != null && !a.baselineLoading),
    );
    if (!ready) return;
    let cancelled = false;
    setSimLoading(true);
    chatbotPickerService.simpleSimulate({
      agents: agents.map(a => ({
        id: a.id,
        label: a.label || a.id,
        cost_per_conv_usd: resolveCostPerConv(a),
        model_label: resolveModelLabel(a),
        convs_per_user_per_month: a.convsPerUserPerMonth,
        files_count: a.filesCount,
        avg_file_mb: a.avgFileMb,
      })),
      active_users: activeUsers,
      num_tenants: numTenants,
      summaries_enabled: summariesEnabled,
      insight_templates_count: insightTemplatesEnabled ? insightTemplatesCount : 0,
      file_churn_pct: fileChurnPct,
      concurrent_pct: concurrentPct,
    }).then(r => { if (!cancelled) setResult(r); })
      .catch((err) => {
        if (!cancelled) antdMessage.error(err?.response?.data?.error ?? 'Failed to simulate');
      })
      .finally(() => { if (!cancelled) setSimLoading(false); });
    return () => { cancelled = true; };
    // Dep array — anything that changes the projection
  }, [
    activeUsers, numTenants, fileChurnPct, concurrentPct,
    summariesEnabled, insightTemplatesEnabled, insightTemplatesCount,
    // Per-agent resolved-cost fingerprint so we re-simulate on each agent change
    agents.map(a => `${a.id}|${resolveCostPerConv(a)}|${a.convsPerUserPerMonth}|${a.filesCount}|${a.avgFileMb}`).join('||'),
  ]);

  // Publish the current scenario snapshot to the parent (CostSimulatorPage)
  // so the Cost Advisor receives the exact params + breakdown the user sees.
  // We rebuild the simple-sim params shape here — same shape that gets POSTed
  // to /simple-simulate and that the backend advisor dispatches on via _kind.
  useEffect(() => {
    if (!onSnapshotChange) return;
    const snapshotParams = {
      _kind: 'simple',
      agents: agents.map(a => ({
        id: a.id,
        label: a.label || a.id,
        cost_per_conv_usd: resolveCostPerConv(a),
        model_label: resolveModelLabel(a),
        convs_per_user_per_month: a.convsPerUserPerMonth,
        files_count: a.filesCount,
        avg_file_mb: a.avgFileMb,
      })),
      active_users: activeUsers,
      num_tenants: numTenants,
      summaries_enabled: summariesEnabled,
      insight_templates_count: insightTemplatesEnabled ? insightTemplatesCount : 0,
      file_churn_pct: fileChurnPct,
      concurrent_pct: concurrentPct,
    };
    onSnapshotChange({ params: snapshotParams, breakdown: result });
  }, [
    onSnapshotChange, result,
    activeUsers, numTenants, summariesEnabled, insightTemplatesEnabled,
    insightTemplatesCount, fileChurnPct, concurrentPct,
    agents.map(a => `${a.id}|${a.label}|${resolveCostPerConv(a)}|${resolveModelLabel(a)}|${a.convsPerUserPerMonth}|${a.filesCount}|${a.avgFileMb}`).join('||'),
  ]);

  // ─── Derived display values ─────────────────────────────────────────
  const chatbotOptions = useMemo(() => chatbots.map(c => ({
    value: c.chatbot_id, label: c.name,
  })), [chatbots]);

  const modelOptions = useMemo(() => pricingPayload.models.map(m => ({
    value: m.id, label: `${m.provider}/${m.name}`,
  })), [pricingPayload.models]);

  function addAgent() {
    setAgents(prev => [...prev, newAgentRow(nextSeed)]);
    setNextSeed(s => s + 1);
  }

  function removeAgent(id: string) {
    setAgents(prev => prev.length > 1 ? prev.filter(a => a.id !== id) : prev);
  }

  function patchAgent(id: string, patch: Partial<SimAgent>) {
    setAgents(prev => prev.map(a => a.id === id ? { ...a, ...patch } : a));
  }

  async function handleSaveScenario() {
    if (!saveName.trim() || !result) return;
    setSaving(true);
    try {
      // Save with `_kind: 'simple'` so the Roadmap plan engine knows to
      // route this scenario through the simple-sim math instead of the
      // Advanced engine. The shape mirrors what /simple-simulate takes.
      const simpleParams = {
        _kind: 'simple' as const,
        agents: agents.map(a => ({
          id: a.id,
          label: a.label || a.id,
          cost_per_conv_usd: resolveCostPerConv(a),
          model_label: resolveModelLabel(a),
          convs_per_user_per_month: a.convsPerUserPerMonth,
          files_count: a.filesCount,
          avg_file_mb: a.avgFileMb,
        })),
        active_users: activeUsers,
        num_tenants: numTenants,
        summaries_enabled: summariesEnabled,
        insight_templates_count: insightTemplatesEnabled ? insightTemplatesCount : 0,
        file_churn_pct: fileChurnPct,
        concurrent_pct: concurrentPct,
      };
      const created = await scenarioService.create(
        saveName.trim(), simpleParams as any, result as any, saveDescription.trim() || null,
      );
      antdMessage.success(`Saved scenario "${created.name}"`);
      setSaveOpen(false);
      setSaveName('');
      setSaveDescription('');
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error ?? t('measuredSim.failedToSaveScenario'));
    } finally {
      setSaving(false);
    }
  }

  // ─── Render — left / right split ────────────────────────────────────
  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0, gap: 0, height: '100%' }}>
      {/* LEFT PANEL — all user inputs */}
      <div style={{
        width: '42%',
        minWidth: 340,
        overflowY: 'auto',
        paddingInlineEnd: token.paddingMD,
        borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
      }}>
        {/* Sections are ALWAYS expanded — no collapse, just titled cards
            so all controls are visible without an extra click. */}
        {([
            {
              key: 'volume',
              label: <Space><TeamOutlined /><Text strong>{t('measuredSim.volume')}</Text></Space>,
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <SliderRow
                    label={t('measuredSim.activeUsersMonth')}
                    /* Slider snaps to multiples of 25 so dragging across the
                       1–10,000 range doesn't ping-pong the projection on
                       every pixel. Users who want a precise value can still
                       type it in the InputNumber on the right. */
                    min={1} max={10000} step={25}
                    value={activeUsers}
                    onChange={setActiveUsers}
                    formatTooltip={(v) => `${v.toLocaleString()} users`}
                    testId="sim-active-users"
                  />
                  <SliderRow
                    label={t('measuredSim.numberOfTenants')}
                    /* Same idea — coarse-snap the slider, leave the
                       InputNumber free for typing exact values. */
                    min={1} max={500} step={5}
                    value={numTenants}
                    onChange={setNumTenants}
                    formatTooltip={(v) => t('counts.tenants', { count: v })}
                    testId="sim-num-tenants"
                  />
                  <div style={{ padding: '6px 10px', background: token.colorBgLayout, borderRadius: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('measuredSim.totalConversationsMonth')}</Text>
                    <div style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtN(result?.volume.total_convs ?? 0)}
                    </div>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {t('measuredSim.sigmaAcrossAgents')}
                    </Text>
                  </div>
                </Space>
              ),
            },
            {
              key: 'agents',
              label: (
                <Space>
                  <AppstoreOutlined />
                  <Text strong>{t('measuredSim.agents')}</Text>
                  <Tag>{agents.length}</Tag>
                </Space>
              ),
              extra: (
                <Button
                  size="small" type="primary" icon={<PlusOutlined />}
                  onClick={(e) => { e.stopPropagation(); addAgent(); }}
                  data-testid="sim-add-agent"
                >
                  {t('costSimulator.addAgent')}
                </Button>
              ),
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {agents.map((a, idx) => (
                    <AgentRow
                      key={a.id}
                      agent={a}
                      index={idx}
                      onChange={(patch) => patchAgent(a.id, patch)}
                      onRemove={() => removeAgent(a.id)}
                      canRemove={agents.length > 1}
                      chatbotOptions={chatbotOptions}
                      modelOptions={modelOptions}
                    />
                  ))}
                </Space>
              ),
            },
            {
              key: 'features',
              label: <Space><ThunderboltOutlined /><Text strong>{t('measuredSim.platformFeatures')}</Text></Space>,
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {/* Conversation titles — always on, fixed assumption.
                      Shown for transparency since it shows up in the receipt. */}
                  <div style={{ padding: '6px 10px', background: token.colorFillAlter, borderRadius: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Space size={4}>
                        <Text strong style={{ fontSize: 12 }}>{t('measuredSim.conversationTitles')}</Text>
                        <Tag style={{ fontSize: 9, margin: 0 }} color="default">{t('measuredSim.haiku')}</Tag>
                        <Tooltip
                          title={
                            <div style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 320 }}>
                              <div style={{ fontWeight: 600, marginBlockEnd: 4 }}>{t('measuredSim.howConversationTitlesAreCalculated')}</div>
                              <div>{t('measuredSim.theAgentAutoGeneratesA')}</div>
                              <ul style={{ paddingInlineStart: 18, marginBlockStart: 4, marginBlockEnd: 4 }}>
                                <li>{t('measuredSim.firesPrefix')}<strong>{t('measuredSim.firesEmph')}</strong>{t('measuredSim.firesSuffix')}</li>
                                <li>{t('measuredSim.tokensPerCall', { in: 500, out: 50 })}</li>
                                <li>{t('measuredSim.haikuRate')}</li>
                              </ul>
                              <div>
                                {t('costSimulator.costTotalConvs2500')}
                              </div>
                            </div>
                          }
                        >
                          <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.65, cursor: 'help' }} />
                        </Tooltip>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        +{fmtUsd(result?.ai.platform_features.title.cost ?? 0)}/mo
                      </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {t('costSimulator.alwaysOn2HaikuCalls')}
                    </Text>
                  </div>

                  {/* Auto-summaries — toggleable */}
                  <div style={{ padding: '6px 10px', background: token.colorFillAlter, borderRadius: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Space size={4}>
                        <Switch
                          size="small"
                          checked={summariesEnabled}
                          onChange={setSummariesEnabled}
                          data-testid="sim-summaries-toggle"
                        />
                        <Text strong style={{ fontSize: 12 }}>{t('measuredSim.autoSummaries')}</Text>
                        <Tag style={{ fontSize: 9, margin: 0 }} color="default">{t('measuredSim.haiku')}</Tag>
                        <Tooltip
                          title={
                            <div style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 360 }}>
                              <div style={{ fontWeight: 600, marginBlockEnd: 4 }}>{t('measuredSim.howAutoSummariesAreCalculated')}</div>
                              <div>{t('measuredSim.backgroundSummarizationCompressesLongConversations')}</div>
                              <ul style={{ paddingInlineStart: 18, marginBlockStart: 4, marginBlockEnd: 4 }}>
                                <li>{t('measuredSim.firesAtPrefix')}<strong>{t('measuredSim.firesAtEmph')}</strong>{t('measuredSim.firesAtSuffix')}</li>
                                <li>{t('measuredSim.averageAssumption')} <strong>{t('measuredSim.threeSummariesPerConv')}</strong></li>
                                <li>{t('measuredSim.tokensPerCall', { in: '1,500', out: 500 })}</li>
                                <li>{t('measuredSim.haikuRate')}</li>
                              </ul>
                              <div>
                                {t('costSimulator.costTotalConvs31500')}
                              </div>
                              <div style={{ marginBlockStart: 6, fontWeight: 600 }}>{t('measuredSim.whyAnAssumption')}</div>
                              <div style={{ marginBlockStart: 2 }}>
                                {t('measuredSim.whyAnAssumptionBody')}
                              </div>
                              <div style={{ marginBlockStart: 6, fontWeight: 600 }}>{t('measuredSim.wantAPreciseNumber')}</div>
                              <ol style={{ paddingInlineStart: 18, marginBlockStart: 4, marginBlockEnd: 0 }}>
                                <li>{t('measuredSim.runTheAgentInProduction')}</li>
                                <li>{t('measuredSim.openPrefix')}<em>{t('measuredSim.openEmph')}</em>{t('measuredSim.openMid')}<em>{t('measuredSim.openEmph2')}</em>{t('measuredSim.openSuffix')}</li>
                                <li>{t('measuredSim.divideByConvCount')}</li>
                              </ol>
                            </div>
                          }
                        >
                          <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.65, cursor: 'help' }} />
                        </Tooltip>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {summariesEnabled
                          ? `+${fmtUsd(result?.ai.platform_features.summary.cost ?? 0)}/mo`
                          : 'disabled'}
                      </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {t('costSimulator.assumes3HaikuSummariesConv')}
                    </Text>
                  </div>

                  {/* Insight templates — on/off toggle mirroring summaries.
                      When OFF, the count input is hidden and the cost is 0. */}
                  <div style={{ padding: '6px 10px', background: token.colorFillAlter, borderRadius: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBlockEnd: 6 }}>
                      <Space size={4}>
                        <Switch
                          size="small"
                          checked={insightTemplatesEnabled}
                          onChange={setInsightTemplatesEnabled}
                          data-testid="sim-templates-toggle"
                        />
                        <Text strong style={{ fontSize: 12 }}>{t('measuredSim.insightTemplates')}</Text>
                        <Tag style={{ fontSize: 9, margin: 0 }} color="default">{t('measuredSim.haiku')}</Tag>
                        <Tooltip
                          title={
                            <div style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 320 }}>
                              <div style={{ fontWeight: 600, marginBlockEnd: 4 }}>{t('measuredSim.howInsightTemplatesAreCalculated')}</div>
                              <div>{t('measuredSim.dailyExtractionRunsHaikuPer')}</div>
                              <ul style={{ paddingInlineStart: 18, marginBlockStart: 4, marginBlockEnd: 4 }}>
                                <li>{t('measuredSim.eachTemplatePrefix')}<strong>{t('measuredSim.eachTemplateEmph')}</strong></li>
                                <li>{t('measuredSim.assumesPrefix')}<strong>{t('measuredSim.assumesEmph')}</strong></li>
                                <li>{t('measuredSim.tokensPerCall', { in: 800, out: 100 })}</li>
                                <li>{t('measuredSim.haikuRate')}</li>
                              </ul>
                              <div>
                                Cost = total_convs × 5 days × N_templates × (800/1M × $1 + 100/1M × $5)
                                <br/>= total_convs × N × $0.0065
                              </div>
                            </div>
                          }
                        >
                          <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.65, cursor: 'help' }} />
                        </Tooltip>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {insightTemplatesEnabled
                          ? `+${fmtUsd(result?.ai.platform_features.insight_templates.cost ?? 0)}/mo`
                          : 'disabled'}
                      </Text>
                    </div>
                    {insightTemplatesEnabled && (
                      <>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBlockEnd: 4 }}>
                          {t('costSimulator.features.insightTemplates')}
                        </Text>
                        <InputNumber
                          size="small"
                          style={{ width: '100%' }}
                          min={0}
                          value={insightTemplatesCount}
                          onChange={v => setInsightTemplatesCount(v ?? 0)}
                          data-testid="sim-templates-count"
                        />
                        <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBlockStart: 4 }}>
                          {t('costSimulator.eachTemplate1DailyHaiku')}
                        </Text>
                      </>
                    )}
                  </div>
                </Space>
              ),
            },
            {
              key: 'infra',
              label: <Space><CloudServerOutlined /><Text strong>{t('measuredSim.infrastructure')}</Text></Space>,
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <SliderRow
                    label={
                      <Space size={4}>
                        <span>{t('measuredSim.fileChurnMo')}</span>
                        <Tooltip
                          title={
                            <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                              <div style={{ fontWeight: 600, marginBlockEnd: 4 }}>{t('measuredSim.whatIsFileChurn')}</div>
                              <div>{t('measuredSim.churnBody')}</div>
                              <div style={{ marginBlockStart: 6, opacity: 0.85 }}>
                                <strong>{t('measuredSim.examples')}</strong><br/>
                                {t('measuredSim.churnExampleStatic')}<br/>
                                {t('measuredSim.churnExampleLiving')}<br/>
                                {t('measuredSim.churnExampleWeekly')}<br/>
                                {t('measuredSim.churnExampleNew')}
                              </div>
                              <div style={{ marginBlockStart: 6, opacity: 0.75, fontStyle: 'italic' }}>
                                {t('measuredSim.churnVsSetup')}
                              </div>
                            </div>
                          }
                        >
                          <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.65, cursor: 'help' }} />
                        </Tooltip>
                      </Space>
                    }
                    min={0} max={100} step={1}
                    value={fileChurnPct}
                    onChange={(v) => setFileChurnPct(v)}
                    formatTooltip={(v) => `${v}%`}
                  />
                  <SliderRow
                    label={t('measuredSim.concurrentUsers')}
                    min={1} max={100} step={1}
                    value={concurrentPct}
                    onChange={setConcurrentPct}
                    formatTooltip={(v) => `${v}%`}
                  />
                  {result && (
                    <div style={{ padding: '6px 10px', background: token.colorBgLayout, borderRadius: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>{t('measuredSim.peakConcurrentUsers')}</Text>
                      <div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {fmtN(result.volume.peak_concurrent)}
                      </div>
                    </div>
                  )}
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {t('measuredSim.filesPerTenantNote')}
                  </Text>
                </Space>
              ),
            },
          ] as Array<{ key: string; label: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode }>).map((sec) => (
            <Card
              key={sec.key}
              title={sec.label}
              extra={sec.extra}
              size="small"
              style={{ marginBlockEnd: 12, background: token.colorBgContainer }}
            >
              {sec.children}
            </Card>
          ))}
      </div>

      {/* RIGHT PANEL — KPIs + chart + table + save */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        paddingInlineStart: token.paddingMD,
      }}>
        {(() => {
          // Decide which "right panel" state to show:
          //   - empty: no agent has produced a usable cost yet (e.g. user
          //     hasn't picked an agent and hasn't typed a manual price)
          //   - loading: result hasn't come back yet
          //   - ready: render KPIs + charts + table + save
          const hasUsableCost = agents.some(a => {
            if (a.source === 'manual') return a.manualCost > 0;
            return a.baseline && a.baseline.baseline.conversation_count > 0;
          });
          if (!hasUsableCost) {
            return (
              <Card>
                <Empty
                  description={
                    <Space direction="vertical" size={6} style={{ textAlign: 'start' }}>
                      <Text strong>{t('measuredSim.fillInTheLeftPanel')}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('measuredSim.bulletPickAgent')}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('measuredSim.bulletSwitchSourcePrefix')}<strong>{t('measuredSim.manual')}</strong>{t('measuredSim.bulletSwitchSourceSuffix')}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        • <Button type="link" size="small" icon={<FolderOpenOutlined />} style={{ padding: 0, height: 'auto' }} onClick={() => setLoadOpen(true)}>
                          {t('costSimulator.loadASavedScenarioFrom')}
                        </Button>
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('measuredSim.thenSetActiveUsers')}
                      </Text>
                    </Space>
                  }
                />
              </Card>
            );
          }
          if (!result) {
            return (
              <Card>
                <Spin /> <Text type="secondary">{t('measuredSim.computingProjection')}</Text>
              </Card>
            );
          }
          return null;
        })()}
        {result && agents.some(a => a.source === 'manual' ? a.manualCost > 0 : a.baseline && a.baseline.baseline.conversation_count > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Action bar — top-right: Load from library + Save */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                icon={<FolderOpenOutlined />}
                onClick={() => setLoadOpen(true)}
                data-testid="sim-load-scenario"
              >
                {t('costSimulator.scenarios.openLibrary')}
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => setSaveOpen(true)}
                disabled={simLoading}
                data-testid="sim-save-scenario"
              >
                {t('costSimulator.saveAsScenario')}
              </Button>
            </div>
            {/* ─── KPI grid — matches Advanced 1:1 (Total / AI only / Infra /
                Peak concurrent / Setup) ──────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <Card size="small">
                <Statistic
                  title={t('measuredSim.totalMonth')}
                  value={result.monthly_recurring}
                  formatter={(v) => fmtUsd(Number(v))}
                  valueStyle={{ color: token.colorPrimary, fontSize: 22 }}
                />
              </Card>
              <Card size="small">
                <Statistic
                  title={t('measuredSim.aiCostsOnly')}
                  value={result.ai.subtotal}
                  formatter={(v) => fmtUsd(Number(v))}
                  valueStyle={{ color: COLORS.mainAI, fontSize: 22 }}
                />
              </Card>
              <Card size="small">
                <Statistic
                  title={t('measuredSim.infrastructureCosts')}
                  value={result.infra.subtotal}
                  formatter={(v) => fmtUsd(Number(v))}
                  valueStyle={{ color: COLORS.infrastructure, fontSize: 22 }}
                />
              </Card>
              <Card size="small">
                <Statistic
                  title={t('measuredSim.peakConcurrentUsers')}
                  value={result.volume.peak_concurrent}
                  valueStyle={{ fontSize: 22 }}
                />
              </Card>
              {result.setup.subtotal > 0 && (
                <Card size="small" style={{ background: '#fef3c7', borderColor: '#fcd34d' }}>
                  <Statistic
                    title={
                      <Tooltip
                        title={
                          <div style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 320 }}>
                            <div style={{ fontWeight: 600, marginBlockEnd: 4 }}>{t('measuredSim.howOneTimeSetupIs')}</div>
                            <div>{t('measuredSim.setupPaidOnce')}</div>
                            <ol style={{ marginBlockStart: 6, marginBlockEnd: 6, paddingInlineStart: 18 }}>
                              <li>
                                <strong>{t('measuredSim.embedding')}</strong>{t('measuredSim.setupEmbeddingFormula')}
                              </li>
                              <li>
                                <strong>{t('measuredSim.enrichment')}</strong>{t('measuredSim.setupEnrichmentFormula')}
                              </li>
                              <li>
                                <strong>{t('measuredSim.bundleContext')}</strong>{t('measuredSim.setupBundleFormula')}
                              </li>
                            </ol>
                            <div>{t('measuredSim.setupMultipliedBy')}</div>
                          </div>
                        }
                      >
                        <span style={{ color: '#92400e' }}>
                          {t('measuredSim.oneTimeSetup')} <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.6 }} />
                        </span>
                      </Tooltip>
                    }
                    value={result.setup.subtotal}
                    formatter={(v) => fmtUsd(Number(v))}
                    valueStyle={{ color: '#b45309', fontSize: 22 }}
                  />
                  <div style={{ fontSize: 10, color: '#92400e', opacity: 0.75 }}>
                    {t('measuredSim.firstMonthTotal', { amount: fmtUsd(result.first_month_total) })}
                  </div>
                </Card>
              )}
            </div>

            {/* ─── Two SEPARATE charts — AI Costs + Infrastructure Costs ─── */}
            <AICostChart result={result} />
            <InfraCostChart result={result} />

            {/* ─── Receipt table — same shape as Advanced ─────────────────── */}
            <ReceiptTable result={result} t={t} />

            {/* ─── Model price reference (16 models) ──────────────────────── */}
            <ModelPriceReference pricing={pricingPayload as any} />
          </div>
        )}
      </div>

      {/* Save modal */}
      <Modal
        open={saveOpen}
        title={t('measuredSim.saveScenario')}
        okText={t('measuredSim.save')}
        confirmLoading={saving}
        onCancel={() => setSaveOpen(false)}
        onOk={handleSaveScenario}
        okButtonProps={{ disabled: !saveName.trim() }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input dir="auto"
            placeholder={t('measuredSim.scenarioNameEGJewishpro')}
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            onPressEnter={handleSaveScenario}
            autoFocus
          />
          <Input.TextArea dir="auto"
            placeholder={t('measuredSim.optionalDescription')}
            value={saveDescription}
            onChange={e => setSaveDescription(e.target.value)}
            rows={3}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('measuredSim.setupMonthOneNote', { recurring: fmtUsd(result?.monthly_recurring ?? 0) })}
          </Text>
        </Space>
      </Modal>

      {/* Load-from-library modal — shows ONLY simple-sim scenarios so we
          don't risk hydrating the simulator with an Advanced-shape payload
          that its inputs can't represent. Clicking Load replaces the
          current agents + volume + features with the saved snapshot. */}
      <Modal
        open={loadOpen}
        title={t('measuredSim.loadASavedScenario')}
        footer={null}
        onCancel={() => setLoadOpen(false)}
        width={760}
      >
        {scenariosLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : savedScenarios.length === 0 ? (
          <Empty
            description={
              <Space direction="vertical">
                <Text>{t('measuredSim.noSavedScenariosYet')}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('costSimulator.buildAProjectionAndClick')}
                </Text>
              </Space>
            }
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, maxHeight: 480, overflowY: 'auto' }}>
            {savedScenarios.map((s) => (
              <ScenarioCard
                key={s.scenario_id}
                scenario={s}
                pricing={pricingPayload as any}
                onLoad={() => loadScenarioIntoSimulator(s)}
              />
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SliderRow — paired slider + InputNumber bound to the same value.
 * Mirrors the Advanced tab's slider pattern so the two simulators feel
 * the same to operate.
 */
interface SliderRowProps {
  label: React.ReactNode;
  min: number;
  max: number;
  /** Slider drag-snap step. Coarse values here make the slider "less
   *  sensitive" so a small drag doesn't jump the projection wildly. */
  step?: number;
  /** Optional override for the InputNumber's up/down step. Defaults to 1
   *  so typed values are always free-form regardless of slider coarseness. */
  inputStep?: number;
  value: number;
  onChange: (v: number) => void;
  formatTooltip?: (v: number) => string;
  testId?: string;
}
function SliderRow({ label, min, max, step = 1, inputStep, value, onChange, formatTooltip, testId }: SliderRowProps) {
  return (
    <div data-testid={testId}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBlockEnd: 4 }}>{label}</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Slider
          min={min} max={max} step={step}
          value={Math.max(min, Math.min(max, value))}
          onChange={(v) => onChange(typeof v === 'number' ? v : value)}
          style={{ flex: 1, marginInline: 4 }}
          tooltip={{ formatter: formatTooltip ? (v) => formatTooltip(v as number) : undefined }}
        />
        <InputNumber
          min={min} max={max} step={inputStep ?? 1}
          value={value}
          onChange={(v) => { if (typeof v === 'number' && !Number.isNaN(v)) onChange(v); }}
          size="small"
          style={{ width: 90, flexShrink: 0 }}
        />
      </div>
    </div>
  );
}

interface AgentRowProps {
  agent: SimAgent;
  index: number;
  onChange: (patch: Partial<SimAgent>) => void;
  onRemove: () => void;
  canRemove: boolean;
  chatbotOptions: Array<{ value: string; label: string }>;
  modelOptions: Array<{ value: string; label: string }>;
}

function AgentRow({
  agent, index, onChange, onRemove, canRemove, chatbotOptions, modelOptions,
}: AgentRowProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const measuredCost = agent.baseline?.baseline.avg_total_cost_usd ?? 0;
  const effectiveCost = resolveCostPerConv(agent);

  return (
    <Card
      size="small"
      style={{ background: token.colorFillAlter, borderColor: token.colorBorderSecondary }}
      title={
        <Space>
          <Tag color="processing">#{index + 1}</Tag>
          <Input dir="auto"
            size="small"
            value={agent.label}
            onChange={e => onChange({ label: e.target.value })}
            placeholder={`Agent ${index + 1}`}
            style={{ width: 180 }}
          />
        </Space>
      }
      extra={
        canRemove && (
          <Tooltip title={t('measuredSim.removeThisAgent')}>
            <Button
              size="small" type="text" danger
              icon={<DeleteOutlined />}
              onClick={onRemove}
            />
          </Tooltip>
        )
      }
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {/* Source toggle */}
        <Segmented<AgentSource>
          size="small"
          block
          value={agent.source}
          onChange={(v) => onChange({ source: v as AgentSource })}
          options={[
            { label: <Space><ExperimentOutlined />{t('measuredSim.measured')}</Space>, value: 'measured' },
            { label: <Space><EditOutlined />{t('measuredSim.manual')}</Space>, value: 'manual' },
          ]}
        />

        {/* Measured: pick agent + date range + optional model swap */}
        {agent.source === 'measured' && (
          <>
            <Row gutter={[8, 8]}>
              <Col xs={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('measuredSim.pickAnAgent')}</Text>
                <Select
                  size="small"
                  showSearch
                  placeholder={t('measuredSim.pickAnAgent2')}
                  style={{ width: '100%' }}
                  options={chatbotOptions}
                  value={agent.chatbotId}
                  onChange={(v) => onChange({ chatbotId: v, baseline: null })}
                  optionFilterProp="label"
                  allowClear
                />
              </Col>
              <Col xs={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t('measuredSim.dateRange')}
                  <Tooltip
                    title={
                      <div style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 280 }}>
                        {t('measuredSim.dateRangeTooltip')}
                      </div>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.6, marginInlineStart: 4, cursor: 'help' }} />
                  </Tooltip>
                </Text>
                <RangePicker
                  size="small"
                  style={{ width: '100%' }}
                  value={agent.dateRange}
                  onChange={(v) => v && onChange({ dateRange: v as [Dayjs, Dayjs], baseline: null })}
                  allowClear={false}
                  // showTime lets the user pick a precise hour:minute window —
                  // useful for isolating a spike or sampling a quiet period.
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  presets={[
                    { label: t('measuredSim.last1h'), value: [dayjs().subtract(1, 'hour'), dayjs()] },
                    { label: t('measuredSim.last6h'), value: [dayjs().subtract(6, 'hour'), dayjs()] },
                    { label: t('measuredSim.last24h'), value: [dayjs().subtract(24, 'hour'), dayjs()] },
                    { label: t('measuredSim.last7d'), value: [dayjs().subtract(7, 'day'), dayjs()] },
                    { label: t('measuredSim.last30d'), value: [dayjs().subtract(30, 'day'), dayjs()] },
                    { label: t('measuredSim.last90d'), value: [dayjs().subtract(90, 'day'), dayjs()] },
                    { label: t('agents.today'), value: [dayjs().startOf('day'), dayjs()] },
                    { label: t('common.yesterday'), value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
                  ]}
                />
              </Col>
              <Col xs={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <SwapOutlined /> {t('measuredSim.modelSwapOptional')}
                  <Tooltip
                    title={
                      <div style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 360 }}>
                        <div style={{ fontWeight: 600, marginBlockEnd: 4 }}>{t('measuredSim.howModelSwapIsCalculated')}</div>
                        <div>
                          {t('measuredSim.modelSwapPrefix')}
                          <strong>{t('measuredSim.modelSwapEmph')}</strong>
                          {t('measuredSim.modelSwapSuffix')}{' '}
                          <code>config/ai_models.json</code>:
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: 11, marginBlock: 6, paddingInline: 8 }}>
                          new_cost = (input × new_input_rate +<br/>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; output × new_output_rate +<br/>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; cache_read × cached_rate +<br/>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; cache_write × write_rate) / 1M
                        </div>
                        <div style={{ fontWeight: 600, marginBlockStart: 6 }}>{t('measuredSim.thisIsAnApproximationNot')}</div>
                        <div style={{ marginBlockStart: 4 }}>
                          We assume the new model will use the <em>same</em> token shape,
                          tool-call frequency, and cache behavior. That's reasonable for same-family
                          swaps (Sonnet → Haiku) but less safe across vendors (Claude ↔ Gemini ↔ GPT)
                          where prompt parsing, reasoning patterns, and caching all differ.
                        </div>
                        <div style={{ marginBlockStart: 6, fontWeight: 600 }}>
                          {t('costSimulator.forAnAccurateAnswer')}
                        </div>
                        <ol style={{ paddingInlineStart: 18, marginBlockStart: 4, marginBlockEnd: 0 }}>
                          <li>{t('measuredSim.switchTheAgentToThe')}</li>
                          <li>{t('measuredSim.comeBackHerePickThe')}</li>
                          <li>{t('measuredSim.costShownPrefix')}<em>{t('measuredSim.costShownEmph')}</em>{t('measuredSim.costShownSuffix')}</li>
                        </ol>
                      </div>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.65, cursor: 'help', marginInlineStart: 4 }} />
                  </Tooltip>
                </Text>
                <Select
                  size="small"
                  allowClear
                  placeholder={t('measuredSim.useAgentsActualModel')}
                  style={{ width: '100%' }}
                  options={modelOptions}
                  value={agent.targetModel}
                  onChange={(v) => onChange({ targetModel: v ?? null, baseline: null })}
                  showSearch
                  optionFilterProp="label"
                />
              </Col>
            </Row>

            {/* Measured baseline display */}
            {agent.baselineLoading && <Spin size="small" />}
            {!agent.baselineLoading && agent.baseline && agent.baseline.baseline.conversation_count > 0 && (
              <div style={{
                padding: '8px 10px',
                background: token.colorBgContainer,
                borderRadius: 4,
                border: `1px solid ${token.colorBorderSecondary}`,
              }}>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {t('measuredSim.measuredConvs', { count: agent.baseline.baseline.conversation_count, model: resolveModelLabel(agent) })}
                </Text>
                <div style={{ fontSize: 16, fontWeight: 600, color: token.colorPrimary, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtUsd(effectiveCost)} <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>/ conv</Text>
                </div>

                {/* Models used in this window — always shown so the user sees
                    exactly which models contributed to the measured cost.
                    Especially important for "Mixed: N models" — each chip
                    shows the model + how many calls came from it. */}
                {agent.baseline.model_distribution.length > 0 && (
                  <div style={{ marginBlockStart: 8 }}>
                    <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBlockEnd: 4 }}>
                      {t('costSimulator.modelsUsedInThisWindow')}
                    </Text>
                    <Space size={4} wrap>
                      {agent.baseline.model_distribution.map((m) => (
                        <Tag
                          key={`${m.provider}/${m.model}`}
                          color={agent.baseline!.baseline_model === m.model ? 'blue' : 'default'}
                          style={{ fontSize: 10, margin: 0 }}
                        >
                          {m.provider}/{m.model} · {t('counts.calls', { count: m.event_count })}
                        </Tag>
                      ))}
                    </Space>
                    {agent.baseline.model_distribution.length > 1 && (
                      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBlockStart: 4, fontStyle: 'italic' }}>
                        {t('costSimulator.mixedModelAgentTheCost')}
                      </Text>
                    )}
                  </div>
                )}

                {/* Model-swap math — visible expanded panel showing exactly
                    how the swap converts the cost. Same tokens × different
                    rates = different cost. */}
                {agent.targetModel && agent.baseline.alt_cost && (
                  <div style={{
                    marginBlockStart: 8,
                    padding: '6px 8px',
                    background: token.colorFillAlter,
                    borderRadius: 4,
                    borderInlineStart: `3px solid ${token.colorPrimary}`,
                  }}>
                    <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                      <SwapOutlined /> Model swap math
                    </Text>
                    <Text style={{ fontSize: 11, display: 'block', marginBlockStart: 2 }}>
                      Original: <strong>{fmtUsd(measuredCost)} / conv</strong> on{' '}
                      {agent.baseline.baseline_provider}/{agent.baseline.baseline_model}
                    </Text>
                    <Text style={{ fontSize: 11, display: 'block' }}>
                      Swapped: <strong>{fmtUsd(agent.baseline.alt_cost.avg_total_cost_usd)} / conv</strong> on{' '}
                      {agent.baseline.alt_cost.provider}/{agent.baseline.alt_cost.model}
                    </Text>
                    <SwapMathTable
                      baseline={agent.baseline.baseline}
                      alt={agent.baseline.alt_cost}
                    />
                  </div>
                )}
              </div>
            )}
            {!agent.baselineLoading && agent.baseline && agent.baseline.baseline.conversation_count === 0 && (
              <Alert
                type="warning" showIcon
                message={t('measuredSim.noMeasuredDataForThis')}
                style={{ fontSize: 11 }}
              />
            )}
          </>
        )}

        {/* Manual: cost-per-conv input */}
        {agent.source === 'manual' && (
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('measuredSim.costPerConversationUsd')}</Text>
            <InputNumber
              size="small"
              style={{ width: '100%' }}
              min={0}
              step={0.001}
              value={agent.manualCost}
              onChange={v => onChange({ manualCost: v ?? 0 })}
              formatter={(v) => `$ ${v}`}
              parser={(v) => Number((v ?? '').toString().replace(/[$ ]/g, ''))}
            />
            <Text type="secondary" style={{ fontSize: 10 }}>
              {t('costSimulator.copyFromInsightsCostBreakdown')}
            </Text>
          </div>
        )}

        {/* Per-agent volume + files */}
        <Row gutter={[8, 8]}>
          <Col xs={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('measuredSim.convsUserMo')}</Text>
            <InputNumber
              size="small" style={{ width: '100%' }} min={0}
              value={agent.convsPerUserPerMonth}
              onChange={v => onChange({ convsPerUserPerMonth: v ?? 0 })}
            />
          </Col>
          <Col xs={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('measuredSim.filesTenant')}</Text>
            <InputNumber
              size="small" style={{ width: '100%' }} min={0}
              value={agent.filesCount}
              onChange={v => onChange({ filesCount: v ?? 0 })}
            />
          </Col>
          <Col xs={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>{t('measuredSim.avgFileMb')}</Text>
            <InputNumber
              size="small" style={{ width: '100%' }} min={0} step={0.1}
              value={agent.avgFileMb}
              onChange={v => onChange({ avgFileMb: v ?? 0 })}
            />
          </Col>
        </Row>
      </Space>
    </Card>
  );
}

// ─── SegmentChart — stacked horizontal bar identical to Advanced ────────
// Hovering one segment dims the others and shows a tooltip with the
// segment's name, dollar amount, and description.
interface SegmentChartProps {
  data: Record<string, number | string>;
  stackKeys: { key: string; color: string }[];
  descriptions: Record<string, string>;
  segmentTotal: number;
}
function SegmentChart({ data, stackKeys, descriptions, segmentTotal }: SegmentChartProps) {
  const { token } = theme.useToken();
  const [hovered, setHovered] = useState<string | null>(null);
  const ContentTooltip = ({ payload, label: _l }: any) => {
    const item = hovered && payload?.find?.((p: any) => p.dataKey === hovered)
      ? payload.find((p: any) => p.dataKey === hovered)
      : payload?.[0];
    if (!item) return null;
    const pct = segmentTotal > 0 ? (item.value / segmentTotal) * 100 : 0;
    return (
      <div style={{
        background: token.colorBgElevated,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        padding: '8px 10px', boxShadow: token.boxShadowSecondary,
        maxWidth: 280, fontSize: 12, color: token.colorText,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color }} />
          {item.dataKey}
        </div>
        <div style={{ fontVariantNumeric: 'tabular-nums', fontSize: 14, marginBlockStart: 4, color: token.colorPrimary }}>
          {fmtUsd(item.value)} <span style={{ fontSize: 11, opacity: 0.65, color: token.colorTextSecondary }}>· {pct.toFixed(1)}%</span>
        </div>
        {descriptions[item.dataKey] && (
          <div style={{ fontSize: 11, opacity: 0.75, lineHeight: 1.5, marginBlockStart: 6 }} dir="auto">
            {descriptions[item.dataKey]}
          </div>
        )}
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={110}>
      <BarChart data={[data]} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 8 }}>
        <XAxis type="number" tickFormatter={(v) => fmtUsd(v as number)} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fontWeight: 600 }} />
        <RechartsTooltip cursor={false} content={<ContentTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {stackKeys.map((s) => (
          <Bar
            key={s.key} dataKey={s.key} stackId="a" fill={s.color}
            fillOpacity={hovered == null || hovered === s.key ? 1 : 0.25}
            onMouseEnter={() => setHovered(s.key)} onMouseLeave={() => setHovered(null)}
            style={{ cursor: 'pointer', transition: 'fill-opacity 0.15s' }}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── AI Costs chart — its own card, mirrors Advanced ────────────────────
function AICostChart({ result }: { result: SimpleSimResponse }) {
  const { t } = useLanguage();
  const aiAgentsSubtotal = result.ai.agents.reduce((s, a) => s + a.ai_recurring, 0);
  // One string per segment, serving three roles at once — the data key, the
  // stack key and the description key must stay identical or the chart loses
  // the series. Naming them once is what makes translating them possible.
  const SEG = {
    mainAI: t('measuredSim.seg.mainAI'),
    titles: t('measuredSim.seg.titles'),
    summaries: t('measuredSim.seg.summaries'),
    insights: t('measuredSim.seg.insightTemplates'),
    files: t('measuredSim.seg.fileProcessingRecurring'),
  };
  const data: Record<string, number | string> = {
    name: t('measuredSim.aiCosts'),
    [SEG.mainAI]: aiAgentsSubtotal,
    [SEG.titles]: result.ai.platform_features.title.cost,
    [SEG.summaries]: result.ai.platform_features.summary.cost,
    [SEG.insights]: result.ai.platform_features.insight_templates.cost,
    [SEG.files]: result.ai.recurring_files_total,
  };
  const stackKeys = [
    { key: SEG.mainAI,    color: COLORS.mainAI },
    { key: SEG.titles,    color: COLORS.internalOps },
    { key: SEG.summaries, color: COLORS.embeddings },
    { key: SEG.insights,  color: COLORS.insights },
    { key: SEG.files,     color: COLORS.flows },
  ];
  const descriptions = {
    [SEG.mainAI]: t('measuredSim.segDesc.mainAI'),
    [SEG.titles]: t('measuredSim.segDesc.titles'),
    [SEG.summaries]: t('measuredSim.segDesc.summaries'),
    [SEG.insights]: t('measuredSim.segDesc.insightTemplates'),
    [SEG.files]: t('measuredSim.segDesc.fileProcessingRecurring'),
  };
  return (
    <Card size="small" title={<span>{t('measuredSim.aiCosts')}</span>}>
      <SegmentChart data={data} stackKeys={stackKeys} descriptions={descriptions} segmentTotal={result.ai.subtotal} />
      <div style={{ fontSize: 11, opacity: 0.55, marginBlockStart: 6, paddingInlineStart: 4 }}>
        <strong>{t('measuredSim.aiTotal')}</strong> {fmtUsd(result.ai.subtotal)}
      </div>
    </Card>
  );
}

// ─── Infrastructure Costs chart — its own card, mirrors Advanced ───────
function InfraCostChart({ result }: { result: SimpleSimResponse }) {
  const { t } = useLanguage();
  const ix = Object.fromEntries(result.infra.rows.map((r) => [r.key, r.cost]));
  const SEG = {
    db: t('measuredSim.seg.database'),
    storage: t('measuredSim.seg.cloudStorage'),
    compute: t('measuredSim.seg.compute'),
    redis: 'Redis',
  };
  const data: Record<string, number | string> = {
    name: t('measuredSim.infrastructureCosts2'),
    [SEG.db]: (ix.dbStorage ?? 0) + (ix.dbCompute ?? 0),
    [SEG.storage]: (ix.gcsStorage ?? 0) + (ix.gcsOps ?? 0) + (ix.gcsEgress ?? 0),
    [SEG.compute]: (ix.computeBase ?? 0) + (ix.computePerUser ?? 0) + (ix.computeConcurrency ?? 0),
    [SEG.redis]: ix.redis ?? 0,
  };
  const stackKeys = [
    { key: SEG.db,      color: '#475569' },
    { key: SEG.storage, color: '#64748b' },
    { key: SEG.compute, color: '#94a3b8' },
    { key: SEG.redis,   color: '#cbd5e1' },
  ];
  const descriptions = {
    [SEG.db]: t('measuredSim.segDesc.database'),
    [SEG.storage]: t('measuredSim.segDesc.cloudStorage'),
    [SEG.compute]: t('measuredSim.segDesc.compute'),
    [SEG.redis]: t('measuredSim.segDesc.redis'),
  };
  return (
    <Card size="small" title={<span>{t('measuredSim.infrastructureCosts2')}</span>}>
      <SegmentChart data={data} stackKeys={stackKeys} descriptions={descriptions} segmentTotal={result.infra.subtotal} />
      <div style={{ fontSize: 11, opacity: 0.55, marginBlockStart: 6, paddingInlineStart: 4 }}>
        <strong>{t('measuredSim.infraTotal')}</strong> {fmtUsd(result.infra.subtotal)}
      </div>
    </Card>
  );
}

// ─── Receipt table — same column layout as Advanced ────────────────────
function ReceiptTable({ result, t }: { result: SimpleSimResponse; t: TFunc }) {
  const { token } = theme.useToken();
  interface Row {
    key: string;
    category: string;
    item: string;
    cost: number;
    formula: string;
    oneTime?: boolean;
  }
  const rows: Row[] = [];
  for (const a of result.ai.agents) {
    if (a.ai_recurring > 0) {
      rows.push({
        key: `${a.id}-ai`, category: a.label,
        item: t('measuredSim.row.agentChat'),
        cost: a.ai_recurring,
        formula: t('measuredSim.row.agentChatFormula', { convs: fmtN(a.convs_per_month), cost: fmtUsd(a.cost_per_conv_usd) }),
      });
    }
    if (a.setup.total > 0) {
      rows.push({
        key: `${a.id}-setup`, category: a.label,
        item: t('measuredSim.row.fileSetup'),
        cost: a.setup.total, formula: a.setup.detail, oneTime: true,
      });
    }
    if (a.recurring_files.total > 0) {
      rows.push({
        key: `${a.id}-rec`, category: a.label,
        item: t('measuredSim.row.fileChurn'),
        cost: a.recurring_files.total, formula: a.recurring_files.detail,
      });
    }
  }
  if (result.ai.platform_features.title.cost > 0) {
    rows.push({
      key: 'pf-title', category: t('measuredSim.row.platformFeatures'),
      item: t('measuredSim.row.titlesHaiku'),
      cost: result.ai.platform_features.title.cost,
      formula: result.ai.platform_features.title.detail,
    });
  }
  if (result.ai.platform_features.summary.cost > 0) {
    rows.push({
      key: 'pf-summary', category: t('measuredSim.row.platformFeatures'),
      item: t('measuredSim.row.summariesHaiku'),
      cost: result.ai.platform_features.summary.cost,
      formula: result.ai.platform_features.summary.detail,
    });
  }
  if (result.ai.platform_features.insight_templates.cost > 0) {
    rows.push({
      key: 'pf-insights', category: t('measuredSim.row.platformFeatures'),
      item: t('measuredSim.row.insightsHaiku'),
      cost: result.ai.platform_features.insight_templates.cost,
      formula: result.ai.platform_features.insight_templates.detail,
    });
  }
  for (const r of result.infra.rows) {
    rows.push({
      key: `infra-${r.key}`, category: t('measuredSim.infrastructureCosts2'),
      item: (t(r.label_i18n_key) || r.key),
      cost: r.cost, formula: r.detail,
    });
  }

  return (
    <Card size="small" title={t('measuredSim.itemizedReceipt')}>
      <Table<Row>
        size="small" pagination={false} rowKey="key"
        // Sort one-time rows to the bottom so monthly recurring math is
        // visually grouped at the top (matches Advanced behavior).
        dataSource={[...rows].sort((a, b) => Number(!!a.oneTime) - Number(!!b.oneTime))}
        tableLayout="fixed"
        expandable={{
          expandedRowRender: (record) => (
            <div style={{
              fontSize: 12, fontFamily: 'monospace', padding: 8,
              background: token.colorFillAlter, borderRadius: token.borderRadius,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              <div style={{ opacity: 0.6, marginBlockEnd: 4 }}>Formula:</div>
              <div>{record.formula}</div>
              <div style={{ marginBlockStart: 6, opacity: 0.6 }}>
                = <strong style={{ color: token.colorPrimary }}>{fmtUsd(record.cost)}</strong> / month
              </div>
            </div>
          ),
          rowExpandable: (record) => Boolean(record.formula),
        }}
        columns={[
          { title: t('skills.category'), dataIndex: 'category', width: '30%', ellipsis: true },
          {
            title: t('costSimulator.table.lineItem'), dataIndex: 'item', ellipsis: true,
            render: (item: string, r: Row) => (
              <span>
                {item}
                {r.oneTime && (
                  <Tag style={{
                    marginInlineStart: 6, fontSize: 9, lineHeight: '14px',
                    padding: '0 5px', border: 'none',
                    background: '#fef3c7', color: '#92400e', fontWeight: 600,
                  }}>
                    {t('costSimulator.oneTime')}
                  </Tag>
                )}
              </span>
            ),
          },
          {
            title: t('costSimulator.table.monthlyCost'), dataIndex: 'cost', align: 'right' as const, width: 130,
            render: (v: number) => (
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmtUsd(v)}</span>
            ),
          },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}>
              <strong>{t('measuredSim.grandTotalMonthRecurring')}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              <strong style={{ color: token.colorPrimary, fontVariantNumeric: 'tabular-nums' }}>
                {fmtUsd(result.monthly_recurring)}
              </strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </Card>
  );
}

// ─── SwapMathTable — breaks down per-component cost diff ────────────────
// Shows the user exactly how the cost converts: same tokens × new rates.
// Each row = one of the four billable categories (input / cache_read /
// cache_write / output).
interface SwapMathTableProps {
  baseline: MeasuredBaselineResponse['baseline'];
  alt: NonNullable<MeasuredBaselineResponse['alt_cost']>;
}
function SwapMathTable({ baseline, alt }: SwapMathTableProps) {
  const { t } = useLanguage();
  // Each row: avg tokens (measured) × per-million rate (target model) ÷ 1M
  // The rate is back-derived from the cost the backend returned, so it
  // always reflects exactly what the pricing engine used.
  const rows = [
    {
      label: t('insights.management.input'),
      tokens: baseline.avg_input_tokens,
      cost: alt.input_cost_usd,
      rate: baseline.avg_input_tokens > 0
        ? (alt.input_cost_usd * 1_000_000) / baseline.avg_input_tokens
        : 0,
    },
    {
      label: t('insights.costTimeline.output'),
      tokens: baseline.avg_output_tokens,
      cost: alt.output_cost_usd,
      rate: baseline.avg_output_tokens > 0
        ? (alt.output_cost_usd * 1_000_000) / baseline.avg_output_tokens
        : 0,
    },
    {
      label: t('insights.messageBreakdown.cacheRead'),
      tokens: baseline.avg_cache_read_tokens,
      cost: alt.cache_read_cost_usd,
      rate: baseline.avg_cache_read_tokens > 0
        ? (alt.cache_read_cost_usd * 1_000_000) / baseline.avg_cache_read_tokens
        : 0,
    },
    {
      label: t('insights.cost.byApiKey.columns.cacheWrite'),
      tokens: baseline.avg_cache_creation_tokens,
      cost: alt.cache_write_cost_usd,
      rate: baseline.avg_cache_creation_tokens > 0
        ? (alt.cache_write_cost_usd * 1_000_000) / baseline.avg_cache_creation_tokens
        : 0,
    },
  ].filter((r) => r.tokens > 0 || r.cost > 0);

  return (
    <table
      style={{
        width: '100%',
        // Bumped from 10px to 13px so the numbers are actually legible.
        fontSize: 13,
        marginBlockStart: 8,
        borderCollapse: 'collapse',
      }}
    >
      <thead>
        <tr style={{ opacity: 0.75, fontSize: 11 }}>
          <th style={{ textAlign: 'start', fontWeight: 600, paddingBlock: 4 }}>{t('measuredSim.category')}</th>
          <th style={{ textAlign: 'end', fontWeight: 600, paddingBlock: 4 }}>{t('measuredSim.avgTokens')}</th>
          <th style={{ textAlign: 'end', fontWeight: 600, paddingBlock: 4 }}>{t('measuredSim.rateM')}</th>
          <th style={{ textAlign: 'end', fontWeight: 600, paddingBlock: 4 }}>= cost</th>
        </tr>
      </thead>
      <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
        {rows.map((r) => (
          <tr key={r.label}>
            <td style={{ paddingBlock: 3 }} dir="auto">{r.label}</td>
            <td style={{ textAlign: 'end', paddingBlock: 3 }}>{fmtN(r.tokens)}</td>
            <td style={{ textAlign: 'end', paddingBlock: 3 }}>${r.rate.toFixed(2)}</td>
            <td style={{ textAlign: 'end', fontWeight: 600, paddingBlock: 3 }}>{fmtUsd(r.cost)}</td>
          </tr>
        ))}
        <tr style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <td colSpan={3} style={{ textAlign: 'end', paddingInlineEnd: 6, fontWeight: 600, paddingBlock: 6, fontSize: 14 }}>
            {t('costSimulator.totalConv')}
          </td>
          <td style={{ textAlign: 'end', fontWeight: 700, paddingBlock: 6, fontSize: 14 }}>
            {fmtUsd(alt.avg_total_cost_usd)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
